import { useState, useRef, useEffect } from "react";
import {
  Settings,
  Bell,
  HelpCircle,
  LogOut,
  ChevronRight,
  Award,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import ProfileService, {
  ProcessedProfileData,
} from "../../services/ProfileService";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<ProcessedProfileData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profileImage")
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Function to refresh profile data
  const _refreshProfile = async () => {
    try {
      setLoading(true);
      const profileData = await ProfileService.getUserProfile();
      setUserProfile(profileData);

      if (profileData.photo) {
        setProfileImage(profileData.photo);
      }
    } catch (_error) {
      // Error handled by ProfileService
    } finally {
      setLoading(false);
    }
  };

  // Fetch profile data from API
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await ProfileService.getUserProfile();
        setUserProfile(profileData);

        // Set profile image if available
        if (profileData.photo) {
          setProfileImage(profileData.photo);
        }
      } catch (_error) {
        // Error handled by ProfileService
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();

    // Listen for storage changes (when preferences are updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user_preferences") {
        fetchProfile(); // Refresh profile data with new preferences
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Listen for page visibility change (when user comes back to this page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchProfile();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Listen for focus event (when navigating back to this page)
    const handleFocus = () => { };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleLogout = () => {
    // Clear ALL Bite Balance storage
    localStorage.removeItem("glucera_access_token");
    localStorage.removeItem("glucera_user_info");
    localStorage.removeItem("glucera_refresh_token");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("onboardingComplete");
    localStorage.removeItem("profileImage");
    localStorage.removeItem("cached_meals");
    localStorage.removeItem("user_preferences");
    localStorage.removeItem("assessment_data");
    localStorage.removeItem("onboarding_completed");
    localStorage.removeItem("glucera_document_id");
    localStorage.removeItem("glucera_onboarding_complete");

    // Clear main app storage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("refreshToken");

    // Clear session storage
    sessionStorage.clear();

    // Get tenant ID from current URL to preserve it in redirect
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const tenantId = pathParts[0]; // First part is tenant ID

    // Force full page reload to clear React Query cache and all state
    // Redirect to login page with tenant ID preserved
    window.location.href = `/glucera/onboardingLoginpage`;
  };

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          message.error("Image size should be less than 5MB");
          return;
        }

        // Upload using ProfileService
        const imageUrl = await ProfileService.uploadProfileImage(file);

        if (imageUrl) {
          setProfileImage(imageUrl);
          // message.success("Profile photo updated successfully!");
        } else {
          message.error("Failed to upload image. Please try again.");
        }
      } catch (_error) {
        message.error("Failed to upload image. Please try again.");
      }
    }
  };

  const menuSections = [
    // {
    //   title: "Account",
    //   items: [
    //     {
    //       icon: Activity,
    //       label: "Connected Devices",
    //       color: "text-chart-3",
    //       bgColor: "bg-accent-light",
    //     },
    //   ],
    // },
    {
      title: "App Settings",
      items: [
        {
          icon: Bell,
          label: "Notifications",
          color: "text-chart-4",
          bgColor: "bg-info-light",
        },
        {
          icon: Settings,
          label: "Settings",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
        // {
        //   icon: Shield,
        //   label: "Privacy & Security",
        //   color: "text-muted-foreground",
        //   bgColor: "bg-muted",
        // },
      ],
    },
    {
      title: "Support",
      items: [
        {
          icon: HelpCircle,
          label: "Help & Support",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
        {
          icon: Award,
          label: "About Bite Balance",
          color: "text-muted-foreground",
          bgColor: "bg-muted",
        },
      ],
    },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="pt-2">
          <h1 className="text-2xl font-bold text-foreground">Profile</h1>
          <p className="text-sm text-muted-foreground">
            Manage your account & preferences
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        )}

        {/* Profile Content - Only show when not loading and data is available */}
        {!loading && userProfile && (
          <>
            {/* User Profile Card */}
            <div className="cursor-pointer rounded-2xl border border-border bg-card p-5 shadow-lg transition-shadow hover:shadow-xl">
              <div className="mb-4 flex items-center gap-4">
                <div className="relative">
                  <div
                    className="h-16 w-16 cursor-pointer overflow-hidden rounded-full border-4 border-blue-100 bg-gradient-to-br from-primary to-chart-1 transition-opacity hover:opacity-80 dark:border-blue-900"
                    onClick={handleImageUpload}
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xl font-bold text-primary-foreground">
                        {userProfile.name
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("") || "HA"}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-foreground truncate">
                    {userProfile.name}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {userProfile.email}
                  </p>
                </div>
              </div>

              <button
                className="h-9 w-full rounded-lg border border-border bg-secondary text-sm font-medium text-secondary-foreground transition-all duration-200 hover:bg-secondary/80 hover:shadow-sm"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.EDIT_PROFILE)
                }
              >
                Edit Profile
              </button>
            </div>

            {/* Stats Overview - Grid Layout for Uniform Cards */}
            <div className="grid grid-cols-3 gap-3">
              {/* Weight Card */}
              <div className="flex min-h-[110px] flex-col rounded-2xl border border-border bg-card p-2 shadow-md">
                <div className="flex flex-1 flex-col items-center justify-center">
                  <div className="flex items-baseline justify-center">
                    <span className="text-xl font-bold text-foreground">
                      {Number(userProfile.weight).toFixed(1)}
                    </span>
                    <span className="ml-1 text-sm font-extrabold text-foreground">
                      {userProfile.weightUnit}
                    </span>
                  </div>
                  <div className="mt-1 text-xs font-extrabold uppercase tracking-wide text-foreground/80">
                    Weight
                  </div>
                </div>
                {/* Fixed height footer for alignment */}
                <div className="flex h-5 items-end justify-center">
                  {Number(userProfile.targetWeight) > 0 ? (
                    <div className="text-[10px] font-semibold text-blue-600 dark:text-blue-400">
                      Target: {Number(userProfile.targetWeight).toFixed(1)}
                    </div>
                  ) : (
                    <div className="text-[10px] text-transparent select-none">Target</div>
                  )}
                </div>
              </div>

              {/* Height Card */}
              <div className="flex min-h-[110px] flex-col rounded-2xl border border-border bg-card p-2 shadow-md">
                <div className="flex flex-1 flex-col items-center justify-center">
                  <div className="flex items-baseline justify-center">
                    {userProfile.heightUnit === "cm" ? (
                      <>
                        <span className="text-xl font-bold text-foreground">
                          {userProfile.heightCm}
                        </span>
                        <span className="ml-1 text-sm font-extrabold text-foreground">
                          cm
                        </span>
                      </>
                    ) : (
                      <span className="text-xl font-bold text-foreground">
                        {userProfile.height}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs font-extrabold uppercase tracking-wide text-foreground/80">
                    Height
                  </div>
                </div>
                {/* Fixed height footer for alignment */}
                <div className="flex h-5 items-end justify-center">
                  <div className="text-[10px] font-semibold text-green-600 dark:text-green-400">
                    {userProfile.gender}
                  </div>
                </div>
              </div>

              {/* Age Card */}
              <div className="flex min-h-[110px] flex-col rounded-2xl border border-border bg-card p-2 shadow-md">
                <div className="flex flex-1 flex-col items-center justify-center">
                  <div className="flex items-baseline justify-center">
                    <span className="text-xl font-bold text-foreground">
                      {userProfile.age}
                    </span>
                    <span className="ml-1 text-sm font-extrabold text-foreground">
                      Yrs
                    </span>
                  </div>
                  <div className="mt-1 text-xs font-extrabold uppercase tracking-wide text-foreground/80">
                    Age
                  </div>
                </div>
                {/* Fixed height footer for alignment */}
                <div className="flex h-5 items-end justify-center">
                  <div className="text-[10px] text-transparent select-none">Placeholder</div>
                </div>
              </div>
            </div>

            {/* Goals Card */}
            <div
              className="cursor-pointer rounded-2xl border border-border bg-card p-5 shadow-lg transition-shadow hover:shadow-xl"
              onClick={() =>
                navigate("/glucera" + GluceraNavigationRoutes.PROFILE_GOALS)
              }
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  Goals & Preferences
                </h3>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>

              <div className="space-y-3">
                {/* Weight Loss Goal */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Weight Loss Goal
                  </span>
                  <span className="font-medium capitalize text-foreground">
                    {userProfile.weightLossGoal}
                  </span>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Activity Level
                  </span>
                  <span className="font-medium capitalize text-foreground">
                    {userProfile.activityLevel}
                  </span>
                </div>
                <div className="h-px bg-border" />

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Diet Type
                  </span>
                  <span className="font-medium text-foreground">
                    {userProfile.dietType}
                  </span>
                </div>
                <div className="h-px bg-border" />

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">
                    Fasting Window
                  </span>
                  <span className="font-medium text-foreground">
                    {userProfile.fastingWindow}
                  </span>
                </div>
              </div>
            </div>

            {/* Menu Sections */}
            {menuSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="space-y-2">
                <h4 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.title}
                </h4>
                <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                  {section.items.map((item, index) => {
                    const Icon = item.icon;
                    const routes: Record<string, string> = {
                      "Personal Information": "/profile/personal-info",
                      "Goals & Preferences": "/profile/goals",
                      // "Connected Devices": "/profile/devices",
                      Notifications: "/profile/notifications",
                      Settings: "/profile/preferences",
                      "Privacy & Security": "/profile/privacy",
                      "Help & Support": "/profile/help",
                      "About Glucera": "/profile/about",
                      "About Bite Balance": "/profile/about",
                    };
                    return (
                      <div key={index}>
                        <button
                          className="flex w-full items-center gap-4 p-4 transition-colors hover:bg-muted/50"
                          onClick={() => {
                            console.log("Clicked item:", item.label);
                            // Handle profile navigation routes
                            if (item.label === "Connected Devices") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_DEVICES
                              );
                            } else if (item.label === "Notifications") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.NOTIFICATIONS
                              );
                            } else if (item.label === "Settings") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_PREFERENCES
                              );
                            } else if (item.label === "Privacy & Security") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_PRIVACY
                              );
                            } else if (item.label === "Help & Support") {
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_HELP
                              );
                            } else if (
                              item.label === "About Glucera" ||
                              item.label === "About Bite Balance"
                            ) {
                              console.log(
                                "Navigating to About page:",
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_ABOUT
                              );
                              navigate(
                                "/glucera" +
                                GluceraNavigationRoutes.PROFILE_ABOUT
                              );
                            } else {
                              const route = routes[item.label];
                              if (route) {
                                navigate(route);
                              }
                            }
                          }}
                        >
                          <div
                            className={`h-10 w-10 rounded-xl ${item.bgColor} flex items-center justify-center`}
                          >
                            <Icon className={`h-5 w-5 ${item.color}`} />
                          </div>
                          <span className="flex-1 text-left font-medium text-foreground">
                            {item.label}
                          </span>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </button>
                        {index < section.items.length - 1 && (
                          <div className="mx-4 h-px bg-border" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex h-12 w-full items-center justify-center rounded-2xl border border-red-300 bg-card text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="mr-2 h-5 w-5" />
              Logout
            </button>

            {/* App Version */}
            <p className="text-center text-xs text-muted-foreground">
              Bite Balance v1.0.0
            </p>
          </>
        )}
      </div>

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <BottomNavBar activeIndex={3} />
    </div>
  );
};

export default ProfilePage;
